import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DashboardService } from 'src/app/core/services/dashboard.service';
import { HttpClientModule } from '@angular/common/http';
import { DashboardResponse } from 'src/app/shared/model/dashboard.model';
import { DisputeResponse, DisputeData } from 'src/app/shared/model/dispute.model';
import { DisputeService } from 'src/app/core/services/dispute.service';
import { UserService } from 'src/app/core/services/user.service';
import { User, UserData, UserResponse } from 'src/app/shared/model/user.model';
import { TableModule } from 'primeng/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { CallService } from 'src/app/core/services/call.service';
import { CallResponse } from 'src/app/shared/model/call.model';
import { domainUrl } from 'src/environments/environment';
import { TABLE_PARAM, USER_TYPES, STATUSES } from 'src/app/shared/constants/common.constants';
import httpErrorCodes from 'src/app/core/common/http_error_codes';
import { CommonService } from 'src/app/shared/services/common.service';
import { SidebarService } from '../../core/services/sidebar.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, HttpClientModule, TableModule, TranslateModule, ToastModule],
  providers: [DashboardService, DisputeService, UserService, TranslateService, CallService],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  tableParams = TABLE_PARAM;

  statuses = STATUSES;

  userTypeConstants = USER_TYPES;

  baseApiUrl = domainUrl.url;

  noDataRow = [{}];

  sidebarCollapsed = false;

  userSpecificData: object | undefined;

  isLoading = true;

  dashboardData!: DashboardResponse['data'];

  disputeData: DisputeResponse['data'] | null = null;

  disputeItems: DisputeData['items'] | [] = [];

  disputePageNumber: DisputeData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  disputePageSize: DisputeData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  userData: UserResponse['data'] | null = null;

  userItems: UserData['items'] | [] = [];

  userPageNumber: UserData['pageNumber'] = TABLE_PARAM.PAGE_NUMBER;

  userPageSize: UserData['pageSize'] = TABLE_PARAM.PAGE_SIZE;

  callResponse!: CallResponse['data'];

  constructor(
    private sidebarService: SidebarService,
    private route: Router,
    private dashboardService: DashboardService,
    private disputeService: DisputeService,
    private userService: UserService,
    private toastService: ToasterService,
    private translateService: TranslateService,
    private callService: CallService,
  ) {}

  ngOnInit() {
    // Subscribe to sidebar state changes
    this.sidebarService.sidebarState$.subscribe((state) => {
      this.sidebarCollapsed = state;
    });
    this.loadDashboardData();
    this.fetchCallDetails();
  }

  goToUserDetailsPage(user: User) {
    this.userSpecificData = user;
    this.route.navigate([`/user-details/${user.id}/${user.userType}`]);
  }

  formatFullName(user: { firstName: string; lastName: string }): string {
    const fullName = [user.firstName, user.lastName].filter((name) => !!name).join(' ');
    return fullName;
  }

  getConvertedDate(utcDate: string) {
    return CommonService.getFormattedDate(utcDate);
  }

  loadDashboardData(): void {
    this.dashboardService.getDashboardData().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.dashboardData = response.data;
          this.getDisputeData(this.disputePageNumber, this.disputePageSize);
          this.getUserData(this.userPageNumber, this.userPageSize);
          this.isLoading = false;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant(
              'DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DASHBOARD_DATA',
            ),
          );
        }
      },
    });
  }

  getDisputeData(pageNumber: number, pageSize: number): void {
    this.disputeService
      .getDisputeData(pageNumber, pageSize, 'CreatedOn', this.tableParams.SORT_DEC_ORDER)
      .subscribe({
        next: (response) => {
          if (response.status === STATUSES.SUCCESS) {
            this.disputeData = response.data;
            this.disputeItems = this.disputeData.items;
          }
        },
        error: (err) => {
          if (err.status !== httpErrorCodes.NOT_FOUND) {
            this.toastService.showErrorToast(
              this.translateService.instant(
                'DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_DISPUTE_DATA',
              ),
            );
          }
        },
      });
  }

  getUserData(pageNumber: number, pageSize: number): void {
    this.userService
      .getUserData(pageNumber, pageSize, 'CreatedOn', this.tableParams.SORT_DEC_ORDER)
      .subscribe({
        next: (response) => {
          if (response.status === STATUSES.SUCCESS) {
            this.userData = response.data;
            this.userItems = this.userData.items;
          }
        },
        error: (err) => {
          if (err.status !== httpErrorCodes.NOT_FOUND) {
            this.toastService.showErrorToast(
              this.translateService.instant(
                'DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_USER_DATA',
              ),
            );
          }
        },
      });
  }

  fetchCallDetails() {
    this.callService.getAllCalls().subscribe({
      next: (response) => {
        if (response.status === STATUSES.SUCCESS) {
          this.callResponse = response.data;
        }
      },
      error: (err) => {
        if (err.status !== httpErrorCodes.NOT_FOUND) {
          this.toastService.showErrorToast(
            this.translateService.instant('DASHBOARD_PAGE.TOAST_MESSAGE.FAILED_TO_LOAD_CALL_DATA'),
          );
        }
      },
    });
  }

  getCallerDetailsById(id: string) {
    const call = this.callResponse.items.find((element) => element.id === +id);
    return call;
  }
}
